<!-- 講座体験談一覧ページ -->
<script setup lang="ts">
  import { usePlanStore } from "~/stores/PlanStore";
  import { DEFAULT_PAGE_SIZE } from "~/consts/contentsConfig";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { PlanReviewInfo } from "~/entities/PlanReviewInfo";

  interface Props {
    planId?: string | undefined;
    userId?: string | undefined;
  }
  const props = withDefaults(defineProps<Props>(), {
    planId: undefined,
    userId: undefined,
  });

  const emit = defineEmits(["onModified", "onDeleted"]);

  const page = ref(1);
  const pageSize = ref(DEFAULT_PAGE_SIZE);

  const snackBarStore = useSnackBarStore();

  const planStore = usePlanStore();
  planStore.fetchOne(props.planId);

  const fetch = async () => {
    if (props.userId) {
      // ユーザーIDがある場合はユーザーの体験談を取得
      await planStore.fetchPlanReviews(1, 1, props.planId, props.userId);
    } else {
      // ユーザーIDがない場合は講座の体験談を取得
      await planStore.fetchPlanReviews(page.value, pageSize.value, props.planId);
    }
  };

  fetch();

  const isOwner = computed<boolean>(() => {
    return planStore.plan?.isOwner || false;
  });

  const computedPage = computed<number>({
    get: () => page.value,
    set: value => {
      page.value = value;
      fetch();
    },
  });

  const showPagination = computed<boolean>(() => {
    if (planStore.planReviews?.totalPages) {
      return planStore.planReviews.totalPages > 1;
    }
    return false;
  });

  // 公開非公開関連
  const onClickPrivate = async (review: PlanReviewInfo, isPrivate: boolean) => {
    try {
      await planStore.updateReviewPrivate(review.reviewId, isPrivate);
      await fetch();

      if (isPrivate) {
        snackBarStore.set("体験談を非公開にしました");
      } else {
        snackBarStore.set("体験談が公開されました");
      }
      emit("onModified");
    } catch (error: any) {
      snackBarStore.setError("体験談の公開設定の変更に失敗しました。時間をおいて再度お試しください。");
    }
  };

  // 削除関連
  const isDeleteDialogOpened = ref(false);
  const deleteReview = ref<PlanReviewInfo>();
  const onClickDelete = (review: PlanReviewInfo) => {
    deleteReview.value = review;
    isDeleteDialogOpened.value = true;
  };
  const onClickDeleteNo = () => {
    isDeleteDialogOpened.value = false;
  };
  const onClickDeleteYes = async () => {
    try {
      await planStore.deleteReview(deleteReview.value?.reviewId ?? "");
      await fetch();
      snackBarStore.set("体験談を削除しました。");
      emit("onModified");
      emit("onDeleted");
    } catch (error: any) {
      snackBarStore.setError("体験談の削除に失敗しました。時間をおいて再度お試しください。");
    } finally {
      isDeleteDialogOpened.value = false;
    }
  };
</script>
<template>
  <div class="mt-0">
    <div v-if="!userId" class="text-center mb-12">
      <span v-if="planStore.plan" class="text-title">{{ planStore.plan?.title }}の体験談一覧</span>
    </div>

    <v-sheet v-for="(review, index) in planStore.planReviews?.reviews" :key="index" color="transparent" class="mb-8">
      <OrganismsPlanReviewCard
        :review="review"
        :is-owner="isOwner"
        @on-private="onClickPrivate"
        @on-delete="onClickDelete"
      />
      <v-divider v-if="index !== (planStore.planReviews?.reviews?.length ?? 0) - 1" class="mt-8"></v-divider>
    </v-sheet>
    <v-sheet v-if="!planStore.planReviews?.reviews?.length" color="transparent" class="no_review">
      講座の体験談はありません
    </v-sheet>

    <v-sheet color="transparent">
      <AtomsPagination
        v-if="showPagination"
        v-model:page="computedPage"
        :total-pages="planStore.planReviews?.totalPages ?? 0"
      />
    </v-sheet>
  </div>
  <MoleculesYesNoDialog
    v-model:is-open="isDeleteDialogOpened"
    :message="`${deleteReview?.title}の体験談を削除してよろしいですか？`"
    yes-color="main-04"
    @click-yes="onClickDeleteYes"
    @click-no="onClickDeleteNo"
  />
</template>

<style lang="scss" scoped>
  .text-title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.8em;
  }
  .no_review {
    color: rgb(var(--v-theme-monochrome-03)) !important;
  }
</style>
